import { Checkbox, CreateTableState, Grid, Icon, Tooltip, Typography } from 'gu-ui-kit'
import clsx from 'clsx'
import { Link } from 'react-router-dom'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { StatusBadge } from '../subcomponents'
import React, { useCallback, useEffect, useState } from 'react'
import formatMoney from 'utils/formatMoney'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import { ROUTES } from 'constants/routes'
import { useTranslation } from 'react-i18next'
import EditableField from 'components/EditableField'
import ActionsColumn from 'modules/Services/Network/subcomponents/AccountsList/subcomponents/ActionsColumn'
import FinanceColumn from 'modules/Services/Network/subcomponents/AccountsList/subcomponents/FinanceColumn'
import styles from 'modules/Services/Network/subcomponents/AccountsList/account-list.module.scss'

export const useTableConfig = (
  {
    display_balance,
    updateSelection,
    selectionIds,
    i18n,
    networkType,
    applyAccountName = () => {},
  },
  data = [],
) => {
  const breakpoints = useBreakpoint()
  const { t } = useTranslation(['services'])
  const pathGen = useGeneratePathI18n()
  const [columnVisibility, setColumnVisibility] = useState()

  const checkboxHandleChange = useCallback(
    ({ target: { checked } }, id = null) => {
      let list = [...selectionIds]

      if (checked) {
        list = [...list, id]
      } else {
        list.splice(list.indexOf(id), 1)
      }

      updateSelection(list)
    },
    [selectionIds],
  )

  useEffect(() => {
    if (!breakpoints.md) {
      setColumnVisibility({
        status: false,
        balance: false,
        finance: false,
      })
    } else if (!breakpoints.xxl) {
      setColumnVisibility({ status: false, balance: display_balance })
    } else {
      setColumnVisibility({ balance: display_balance })
    }
  }, [breakpoints, display_balance])

  const toggleAllRows = useCallback(
    ({ target: { checked } }) => updateSelection(checked ? data.map(({ id }) => id) : []),
    [selectionIds, data],
  )

  const tableConfig = {
    columns: [
      {
        id: 'checkbox',
        header: () => (
          <Grid
            justifyContent="center"
            className={styles['accounts-list__cell_checkbox-wrapper']}
          >
            <Checkbox
              checked={selectionIds.length === data.length}
              onChangeHandle={toggleAllRows}
            />
          </Grid>
        ),
        cell: ({
          row: {
            original: { id },
          },
        }) => (
          <Grid
            justifyContent="center"
            className={styles['accounts-list__cell_checkbox-wrapper']}
          >
            <Checkbox
              checked={!!selectionIds.find((item) => item === id)}
              onChangeHandle={(e) => checkboxHandleChange(e, id)}
            />
          </Grid>
        ),

        className: clsx(styles['accounts-list__cell'], styles['accounts-list__cell_checkbox']),
      },
      {
        id: 'name',
        header: t('services::account_list.table.head.name'),
        className: clsx(styles['accounts-list__cell'], styles['accounts-list__cell_name']),
        cell: (props) => {
          const {
            row: {
              original: {
                id,
                login,
                name,
                status,
                advertiser_info,
                balance: { amount, currency },
              },
            },
          } = props
          return (
            <Grid
              direction={'column'}
              gap={8}
              alignItems="flex-start"
            >
              <Grid
                className={styles['accounts-list__account']}
                fluid={false}
                direction="column"
                alignItems="flex-start"
                justifyContent="start"
                gap={4}
              >
                {!(status === 'deleted' || status === 'block') &&
                  advertiser_info !== null &&
                  advertiser_info?.status !== 'valid' && (
                    <Tooltip
                      title={t(
                        'services::account_list.advertiser_info_not_full_filled.tooltip.text',
                      )}
                      placement={'top'}
                      tooltipClassName={styles['accounts-list__advertiser-info_invalid-tooltip']}
                      className={styles['accounts-list__advertiser-info_invalid']}
                    >
                      <Link
                        to={pathGen(ROUTES.SERVICES_NETWORK_ACCOUNT_ADVERTISER_INFO_EDIT, {
                          networkType,
                          accountId: id,
                        })}
                      >
                        <Icon
                          size={'sm'}
                          name={'status warning2'}
                          bgColor={'Label-Onsurface'}
                          color={'Accent-Error-Primary'}
                          status={'advertiser_info_not_full_filled'}
                        />
                      </Link>
                    </Tooltip>
                  )}
                <Typography
                  variant={'Body-Forms-15'}
                  component={
                    <>
                      <EditableField
                        className={styles['accounts-list__edit-account-name']}
                        id={id}
                        name={name}
                        login={login}
                        apply={applyAccountName}
                        editIconClassName={styles['edit-account__edit-icon']}
                      >
                        <Link
                          className={clsx(
                            styles['accounts-list__account-name-link'],
                            styles[`accounts-list__account-login_status-${status}`],
                          )}
                          title={name ?? login}
                          to={pathGen(ROUTES.SERVICES_NETWORK_ACCOUNT, {
                            i18n,
                            networkType,
                            accountId: id,
                          })}
                        >
                          {name ?? login}
                        </Link>
                      </EditableField>
                    </>
                  }
                />

                <Typography
                  variant={'Body-10'}
                  component={'div'}
                  noMargin
                  color={'Label-Secondary'}
                >
                  <Link
                    className={clsx(
                      styles['accounts-list__ellipsis'],
                      styles['accounts-list__account-login'],
                      styles[`accounts-list__account-login_status-${status}`],
                    )}
                    to={pathGen(ROUTES.SERVICES_NETWORK_ACCOUNT, {
                      i18n,
                      networkType,
                      accountId: id,
                    })}
                  >
                    <span className={styles['accounts-list__ellipsis']}>{login}</span>
                  </Link>
                </Typography>
              </Grid>

              {display_balance && !breakpoints.md && (
                <Typography
                  variant={'Body-10'}
                  noMargin
                  className={styles['accounts-list__account-balance']}
                  dangerouslySetInnerHTML={{
                    __html: formatMoney(amount, currency),
                  }}
                />
              )}

              {!breakpoints.xxl && (
                <StatusBadge
                  variant={'Body-10'}
                  size={'xs'}
                  status={status}
                  rounded
                  className={styles['accounts-list__account-status']}
                />
              )}

              {!breakpoints.md && (
                <FinanceColumn
                  {...props}
                  networkType={networkType}
                  i18n={i18n}
                />
              )}
            </Grid>
          )
        },
      },
      {
        id: 'status',
        header: t('services::account_list.table.head.status'),
        className: clsx(styles['accounts-list__cell'], styles['accounts-list__cell_status']),
        cell: ({
          row: {
            original: {
              status,
              //  advertiser_info, id
            },
          },
        }) => (
          <Grid
            gap={8}
            direction={'column'}
          >
            <div>
              <StatusBadge
                size={'xs'}
                variant={'Body-10'}
                status={status}
                rounded
                className={styles['accounts-list__account-status']}
              />
            </div>
          </Grid>
        ),
      },
      {
        id: 'balance',
        header: t('services::account_list.table.head.balance'),
        className: clsx(styles['accounts-list__cell'], styles['accounts-list__cell_balance']),
        cell: ({
          row: {
            original: {
              balance: { amount, currency },
            },
          },
        }) => (
          <Grid
            className={styles['accounts-list__account-balance-wrapper']}
            alignItems="start"
            justifyContent="start"
            direction="column"
            l={{
              direction: 'row',
              alignItems: 'center',
              justifyContent: 'between',
            }}
          >
            {breakpoints.md && (
              <Grid
                fluid={false}
                direction="column"
                className={styles['accounts-list__account-balance-column']}
              >
                <Typography
                  variant={'Body-3'}
                  noMargin
                  className={styles['accounts-list__account-balance']}
                  dangerouslySetInnerHTML={{
                    __html: formatMoney(amount, currency),
                  }}
                />
              </Grid>
            )}
          </Grid>
        ),
      },
      {
        id: 'finance',
        header: ' ',
        isPlaceholder: true,
        className: clsx(styles['accounts-list__cell'], styles['accounts-list__cell_finance']),
        cell: (props) => (
          <FinanceColumn
            {...props}
            networkType={networkType}
            i18n={i18n}
          />
        ),
      },
      {
        id: 'actions',
        header: ' ',
        isPlaceholder: true,
        className: clsx(styles['accounts-list__cell'], styles['accounts-list__cell_actions']),
        cell: (props) => (
          <ActionsColumn
            {...props}
            networkType={networkType}
            i18n={i18n}
          />
        ),
      },
    ],
    data,
    state: {
      columnVisibility,
    },
    showHeader: true,
    showFooter: false,
    className: styles['accounts-list__table'],
  }

  return {
    tableState: CreateTableState(tableConfig, data),
    tableConfig,
  }
}
